/**
 * O ZIP dos dados (Firestore e Storage juntos), do lado do navegador.
 *
 * O formato, a geração e a conferência são do backend (zepadoca-functions,
 * `utils/dataTransfer.ts`). O ZIP não passa por callable, que aceita no máximo
 * 32 MiB: a exportação fica no Storage e o navegador a baixa por um link, e a
 * importação sobe o ZIP direto ao Storage, em `imports/{uid}/`, antes de o
 * backend conferir e importar.
 */

import { ref, uploadBytesResumable, type UploadTask } from "firebase/storage";
import { auth, storage } from "./firebase";
import type { ImportApplyResult, ImportBackupResult, InspectImportResult } from "./callables";

/** O mesmo teto da regra do Storage para `imports/{uid}/`. */
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024 * 1024;

export type StepState = "pending" | "running" | "done" | "failed";

/** Os dois passos da troca (o backup e a importação), e o que cada um deixou. */
export interface ImportProgress {
  backup: StepState;
  apply: StepState;
  backupResult?: ImportBackupResult;
  result?: ImportApplyResult;
  error?: string;
}

/** O ZIP escolhido para importar, do envio até a conferência do servidor. */
export type PickedZip = { name: string; size: number } & (
  | { state: "uploading"; fraction: number }
  | { state: "inspecting" }
  | { state: "ready"; path: string; inspection: InspectImportResult }
  | { state: "failed"; error: string }
);

/** Recusa no navegador o que o Storage recusaria depois do envio inteiro. */
export function checkImportFile(file: File): string | null {
  const isZip = /\.zip$/i.test(file.name) || /zip/i.test(file.type);
  if (!isZip) return "Escolha o arquivo .zip gerado pela exportação do painel.";
  if (file.size === 0) return "O arquivo está vazio.";
  if (file.size > MAX_IMPORT_BYTES) {
    return `O arquivo tem ${formatBytes(file.size)}, e o máximo que a importação aceita é ${formatBytes(MAX_IMPORT_BYTES)}.`;
  }
  return null;
}

/**
 * Envia o ZIP ao Storage em partes (`uploadBytesResumable`), com o andamento.
 * O tipo vai fixo em `application/zip`: o navegador manda o que o sistema diz
 * (no Windows, `application/x-zip-compressed`), e a regra do Storage só
 * aceita esse.
 */
export function uploadImportZip(
  file: File,
  onProgress: (fraction: number) => void,
): { task: UploadTask; done: Promise<string> } {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Sua sessão expirou. Entre novamente.");
  const path = `imports/${uid}/${Date.now()}.zip`;
  const task = uploadBytesResumable(ref(storage, path), file, { contentType: "application/zip" });
  const done = new Promise<string>((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => onProgress(snapshot.totalBytes ? snapshot.bytesTransferred / snapshot.totalBytes : 0),
      reject,
      () => resolve(path),
    );
  });
  return { task, done };
}

/** Traduz o erro do envio do ZIP para o que o administrador precisa ler. */
export function describeZipUploadError(error: unknown): string {
  const code = (error as { code?: string })?.code ?? "";
  if (code === "storage/canceled") return "Envio cancelado.";
  if (code === "storage/unauthorized") {
    return "O Storage recusou o envio. Ele aceita só arquivo .zip de até 10 GB, enviado por administrador; se a sua conta é de administrador, saia e entre de novo para atualizar o acesso.";
  }
  if (code === "storage/retry-limit-exceeded" || code === "storage/unknown") {
    return "Não foi possível enviar o arquivo. Verifique a conexão e tente de novo.";
  }
  return error instanceof Error && error.message
    ? error.message
    : "Não foi possível enviar o arquivo.";
}

/**
 * Começa o download sem sair da página, para o backup da importação, que
 * chega sem clique (minutos depois da confirmação). O link é de outro
 * domínio, o do Storage, e quem faz o navegador baixar em vez de abrir é o
 * `Content-Disposition: attachment` gravado no ZIP. O iframe escondido segura
 * a navegação: aberto na própria aba, o link levaria embora a página e o
 * andamento da importação. A tela mostra o link também, para o caso de o
 * navegador bloquear.
 */
export function startBackgroundDownload(url: string): void {
  const frame = document.createElement("iframe");
  frame.hidden = true;
  frame.src = url;
  document.body.appendChild(frame);
  // Tirar o iframe antes da resposta cancelaria o download; depois, não.
  window.setTimeout(() => frame.remove(), 10 * 60_000);
}

export function formatBytes(bytes: number): string {
  const format = (value: number) => value.toFixed(1).replace(".", ",");
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${format(bytes / 1024)} KB`;
  if (bytes < 1024 ** 3) return `${format(bytes / 1024 ** 2)} MB`;
  return `${format(bytes / 1024 ** 3)} GB`;
}
